import { useEffect, useRef } from "react";
import { useToast } from "../components/Toast.js";
import { AWAY_MS, CHECK_EVERY_MS, newBuildDeployed, onReturn, pageIsBusy } from "./freshness.js";

/**
 * Wires lib/freshness.ts to the page: page visibility, a timer while visible,
 * and the toast that offers a reload. `refetch` is called to re-fetch the
 * screen in view; see freshness.ts for when.
 */
export function useFreshness(refetch: () => void): void {
  const toast = useToast();
  const latest = useRef({ toast, refetch });
  latest.current = { toast, refetch };

  useEffect(() => {
    let hiddenAt: number | null = document.visibilityState === "hidden" ? Date.now() : null;
    let picking = false;
    let offered = false;

    const offer = () => {
      if (offered) return;
      offered = true;
      latest.current.toast(
        "A new version is ready.",
        { label: "Reload", run: () => window.location.reload() },
        { sticky: true, icon: "refresh" },
      );
    };

    // A file input opening the camera or the photo library hides the app; the
    // return from it carries the photo, so it is not a return to refresh on.
    const onClick = (event: MouseEvent) => {
      if (event.target instanceof HTMLInputElement && event.target.type === "file") picking = true;
    };

    const onVisibility = async () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      const awayMs = hiddenAt === null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;
      const busy = picking || pageIsBusy();
      picking = false;
      const action = onReturn({ awayMs, newBuild: awayMs >= AWAY_MS && (await newBuildDeployed()), busy });
      if (action === "reload") window.location.reload();
      else if (action === "offer-reload") offer();
      else if (action === "refetch") latest.current.refetch();
    };

    const timer = window.setInterval(() => {
      if (document.visibilityState !== "visible" || offered) return;
      void newBuildDeployed().then((fresh) => fresh && offer());
    }, CHECK_EVERY_MS);

    document.addEventListener("click", onClick, true);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
}
